import { Component, effect, inject, input } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { CommonModule } from '@angular/common';
import { forkJoin } from 'rxjs';
import { TabComponent } from '../../../components/tab/tab.component';
import { TabLayoutComponent } from '../../../components/tab-layout/tab-layout.component';
import { PieComponent } from '../../../components/pie/pie.component';
import { ConfigService } from '../../../services/config.service';
import { ChartData } from '../../../interfaces/global/chart';
import { environment } from '../../../../environments/environment';

interface TypeRow {
  'Customer Type': string;
  Count: number;
  Percentage: number;
}

interface SpecialityRow {
  Speciality: string;
  Count: number;
  Percentage: number;
}

interface TableResponse<T> {
  success: boolean;
  data: {
    rows: T[];
  };
}

@Component({
  selector: 'app-accumulative-list-tab',
  imports: [CommonModule, TabLayoutComponent, PieComponent],
  templateUrl: './accumulative-list-tab.component.html',
  styleUrl: './accumulative-list-tab.component.scss',
  providers: [
    { provide: TabComponent, useExisting: AccumulativeListTabComponent },
  ],
})
export class AccumulativeListTabComponent extends TabComponent {
  private http = inject(HttpClient);
  private configService = inject(ConfigService);

  /**
   * Optional user ID. When provided, fetches user-specific data.
   * When omitted, fetches global/aggregated data.
   */
  userId = input<string>();

  /**
   * Optional custom tab name key for dictionary lookup.
   * When omitted, defaults to 'AccumulativeList'.
   */
  tabNameKey = input<string>();

  countData: ChartData = { chartLabel: '', itemLabels: [], itemValues: [] };
  specialitiesData: ChartData = {
    chartLabel: '',
    itemLabels: [],
    itemValues: [],
  };

  constructor() {
    super();
    effect(() => {
      const nameKey = this.tabNameKey() || 'AccumulativeList';
      this.name = this.dictionaryService.get(nameKey);
    });
  }

  public async main(): Promise<void> {
    this.loading.set(true);

    const body = {
      ...this.configService.getApiDateTimeRange(),
      page: 1,
      pageSize: 10,
      getTotalCount: 1,
      getAvailableFilters: 1,
      sortBy: [{ 'Count': 'desc' }],
      filters: {},
    };

    const userId = this.userId();
    const buildUrl = (baseEndpoint: string) => {
      const url = `${environment.apiBaseUrl}${baseEndpoint}`;
      return userId ? `${url}/${userId}` : url;
    };

    return new Promise<void>((resolve) => {
      forkJoin({
        types: this.http.post<TableResponse<TypeRow>>(
          buildUrl('/customers/tables/customer-types'),
          body,
        ),
        specialities: this.http.post<TableResponse<SpecialityRow>>(
          buildUrl('/customers/tables/customer-specialities'),
          body,
        ),
      }).subscribe({
        next: ({ types, specialities }) => {
          if (types.success) {
            this.countData = this.buildCountChart(types.data.rows);
          }
          if (specialities.success) {
            this.specialitiesData = this.buildSpecialitiesChart(
              specialities.data.rows,
            );
          }
          resolve();
        },
        error: () => {
          this.error = 'Failed to load data';
          resolve();
        },
      });
    });
  }

  private buildCountChart(rows: TypeRow[]): ChartData {
    const filtered = rows.filter(
      (r) => r['Customer Type'] && r['Customer Type'] !== '-',
    );
    return {
      chartLabel: this.dictionaryService.get('Count'),
      itemLabels: filtered.map((r) => r['Customer Type']),
      itemValues: filtered.map((r) => r.Count),
    };
  }

  private buildSpecialitiesChart(rows: SpecialityRow[]): ChartData {
    const filtered = rows.filter(
      (r) => r.Speciality && r.Speciality !== '-' && r.Speciality.trim() !== '',
    );
    return {
      chartLabel: this.dictionaryService.get('Specialities'),
      itemLabels: filtered.map((r) => r.Speciality),
      itemValues: filtered.map((r) => r.Count),
    };
  }
}
